import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { styled } from '@mui/material/styles';

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(0.5),
  backgroundColor: 'white',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const MaterialComparisonChart = ({ 
  masterTypes = {}, 
  iesTypes = {}, 
  plotData = null,
  title = "plot_symbol_type_comparison"
}) => {
  // Si hay plotData del backend, usarlo; sino, aproximación
  let masterData, similitudData, diferenciaData, typesArray;

  if (plotData && plotData.master_counts && plotData.ies_counts_by_state) {
    // Usar datos reales del backend
    const masterCounts = plotData.master_counts;
    const iesCountsByState = plotData.ies_counts_by_state;
    
    // Obtener todos los tipos
    const allTypes = new Set([
      ...Object.keys(masterCounts),
      ...Object.keys(iesCountsByState)
    ]);
    typesArray = Array.from(allTypes).sort();
    
    masterData = typesArray.map(type => masterCounts[type] || 0);
    similitudData = typesArray.map(type => iesCountsByState[type]?.Similitud || 0);
    diferenciaData = typesArray.map(type => iesCountsByState[type]?.Diferencia || 0);
  } else {
    // Aproximación anterior
    const allTypes = new Set([
      ...Object.keys(masterTypes),
      ...Object.keys(iesTypes)
    ]);
    typesArray = Array.from(allTypes).sort();
    
    masterData = typesArray.map(type => masterTypes[type] || 0);
    similitudData = typesArray.map(type => {
      const totalIes = iesTypes[type] || 0;
      return Math.round(totalIes * 0.6);
    });
    diferenciaData = typesArray.map(type => {
      const totalIes = iesTypes[type] || 0;
      return totalIes - similitudData[typesArray.indexOf(type)];
    });
  }

  // Truncar etiquetas largas para mejor visualización
  const truncatedLabels = typesArray.map(type => 
    type.length > 10 ? type.substring(0, 10) + '...' : type
  );

  return (
    <ChartContainer>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Chip 
            label={`${typesArray.length} tipos`} 
            size="small" 
            color="primary" 
            variant="outlined"
          />
        </Box>
      </Box>

      <Box sx={{ 
        flex: 1, 
        minHeight: 0, 
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <BarChart
          height={280}
          series={[
            {
              data: masterData,
              label: 'Master',
              id: 'masterSeries',
              color: '#1976d2',
            },
            {
              data: similitudData,
              label: 'IES-Similitud',
              id: 'similitudSeries', 
              color: '#4caf50',
              stack: 'ies',
            },
            {
              data: diferenciaData,
              label: 'IES-Diferencia',
              id: 'diferenciaSeries', 
              color: '#fbbf24',
              stack: 'ies',
            },
          ]}
          xAxis={[
            {
              data: truncatedLabels,
              scaleType: 'band',
              categoryGapRatio: 0.3,
              barGapRatio: 0.1,
            },
          ]}
          yAxis={[
            {
              label: 'Cantidad',
            },
          ]}
          margin={{
            left: 0,
            right: 20,
            top: 10,
            bottom: 80,
          }}
          slotProps={{
            tooltip: {
              trigger: 'item',
              itemContent: ({ dataIndex, series }) => {
                if (dataIndex !== undefined && typesArray[dataIndex]) {
                  const fullTypeName = typesArray[dataIndex];
                  const seriesName = series.label;
                  const value = series.data[dataIndex];
                  return (
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {fullTypeName}
                      </Typography>
                      <Typography variant="caption">
                        {seriesName}: {value}
                      </Typography>
                    </Box>
                  );
                }
                return null;
              },
            },
          }}
          legend={{
            direction: 'row',
            position: { vertical: 'top', horizontal: 'middle' },
            padding: 10,
          }}
        />
      </Box>
    </ChartContainer>
  );
};

export default MaterialComparisonChart;